import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import classNames from 'classnames'

import { useEffect, useState, useRef, useCallback, useMemo } from 'react'

import * as d3 from 'd3'

import { decodeText, formatAmounts, pluralize } from '@aytm/app/stats/lib/util/helpers'
import { truncateText } from '@aytm/app/stats/page/components/lib/utils'
import { isUserViewerOrGuest } from '@aytm/app/stats/page/question-views/hm/lib/utils'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

import style from '@aytm/app/stats/page/question-views/hm/components/chart/components/cluster-popup/styles/ClusterPopup.module.scss'

const POPUP_SHOW_DELAY = 50

/**
 * Check if cluster may have OE (open-ended) comments
 */
const isClusterMayHaveOE = (cluster, batches, answer) => {
  if (cluster?.stat?.comments > 0) {
    return true
  }

  const oeBatches = batches?.filter(batch => batch.comments) || []

  if (oeBatches.length > 0) {
    const allPins = answer?.heatmap?.points

    if (allPins) {
      const clustersPoints = cluster?.points || []
      return allPins.some((point, index) => {
        const batchIndex = parseInt(point.batch.split('b')[1], 10) - 1
        return clustersPoints.includes(index) && batches[batchIndex]?.comments
      })
    }

    return true
  }

  return false
}

const ClusterPopup = ({
  id,
  cluster,
  clusterEl,
  image,
  scale,
  config,
  batches,
  answer,
  userRoles,
  onEditCluster,
  onMouseEnter,
  onMouseLeave,
  onCommentsClick
}) => {
  const popupRef = useRef(null)

  const [position, setPosition] = useState({ left: 0, top: 0 })
  const [placement, setPlacement] = useState('')
  const [isVisible, setIsVisible] = useState(false)

  const imageWidth = image?.original?.width || 0
  const imageHeight = image?.original?.height || 0

  const isViewerOrGuest = isUserViewerOrGuest(userRoles)

  // the title is stored encoded; the shared cut works on its characters and hands back markup, and
  // this name is drawn as text
  const title = decodeText(truncateText(cluster?.title || '', 70))
  const respondentsAmount = cluster?.stat?.respondents || 0
  const pointsAmount = cluster?.stat?.points || 0
  const commentsAmount = cluster?.stat?.comments || 0
  const color = cluster?.color
  const isEditable = !(cluster?.editor || false)

  const hasOE = useMemo(() => isClusterMayHaveOE(cluster, batches, answer), [cluster, batches, answer])

  const configBatches = config?.batches || []
  const commentsLinkId = configBatches.length === 1 ? `${id}_${configBatches?.[0].split('b')?.[1]}` : id

  const calcPosition = useCallback(() => {
    if (!clusterEl || !popupRef.current) return

    const popupWidth = popupRef.current.getBoundingClientRect().width || 140
    const popupHeight = popupRef.current.getBoundingClientRect().height || 80

    const border = cluster.border.map(item => [item[0], imageHeight - item[1]])
    const bbox = clusterEl.getBBox()

    const topDist = Math.max(0, bbox.y)
    const leftDist = Math.max(0, bbox.x)
    const bottomDist = Math.max(0, imageHeight - topDist - bbox.height)
    const rightDist = Math.max(0, imageWidth - leftDist - bbox.width)

    const contentWidth = 748
    const leftOffset = imageWidth * scale < contentWidth ? (contentWidth - imageWidth * scale) / 2 : 0

    const maxDist = Math.max(topDist, bottomDist, rightDist, leftDist)

    let placementClass = ''
    if (maxDist === topDist) placementClass = 'top'
    else if (maxDist === bottomDist) placementClass = 'bottom'
    else if (maxDist === leftDist) placementClass = 'left'
    else if (maxDist === rightDist) placementClass = 'right'

    const hull = d3.polygonHull(border)
    const popupPoint = d3.polygonCentroid(hull)

    let coords = [0, 0]

    if (placementClass === 'top') {
      coords = [leftOffset + popupPoint[0] * scale - popupWidth / 2, popupPoint[1] * scale - popupHeight - 20]
    } else if (placementClass === 'bottom') {
      coords = [leftOffset + popupPoint[0] * scale - popupWidth / 2, popupPoint[1] * scale + 20]
    } else if (placementClass === 'left') {
      coords = [leftOffset + popupPoint[0] * scale - popupWidth - 20, popupPoint[1] * scale]
    } else if (placementClass === 'right') {
      coords = [leftOffset + popupPoint[0] * scale + 20, popupPoint[1] * scale]
    } else {
      coords = [leftOffset + popupPoint[0] * scale - popupWidth / 2, popupPoint[1] * scale - popupHeight]
    }

    setPosition({ left: coords[0], top: coords[1] })
    setPlacement(placementClass)
  }, [clusterEl, cluster, imageWidth, imageHeight, scale])

  useEffect(() => {
    if (!clusterEl) return

    // Initial calc without popup dimensions
    calcPosition()

    // Recalc after render with actual dimensions
    const timer = setTimeout(() => {
      calcPosition()
      setIsVisible(true)
    }, POPUP_SHOW_DELAY)

    return () => clearTimeout(timer)
  }, [clusterEl, calcPosition])

  if (!cluster) return null

  return (
    <div
      ref={popupRef}
      className={classNames(style.popup, style[placement], { [style.visible]: isVisible })}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ left: position.left, top: position.top }}
    >
      <div className={style.head} style={{ borderBottomColor: color }}>
        <div className={style.name}>{title}</div>
        {isEditable ? (
          !isViewerOrGuest && (
            <i
              className={classNames('icon-aytmfd-pencil', style.action)}
              aria-label="Edit cluster"
              {...pressable(() => onEditCluster(cluster.uid))}
            />
          )
        ) : (
          <i
            className={classNames('icon-aytmfd-lock', style.action, style.locked)}
            title="Clusters created in the Survey Editor are not editable during analysis."
          />
        )}
      </div>
      <div className={style.body}>
        <ul>
          <li>
            <b>{formatAmounts(pointsAmount)}</b> {pluralize(pointsAmount, 'pin', 'pins')} from
          </li>
          <li>
            <b>{formatAmounts(respondentsAmount)}</b> {pluralize(respondentsAmount, 'respondent', 'respondents')}
          </li>
          {hasOE && (
            <li>
              <b>{formatAmounts(commentsAmount)}</b> {pluralize(commentsAmount, 'comment', 'comments')}
              {commentsAmount > 0 && (
                <a
                  id={`comments_${commentsLinkId}`}
                  data-cluster-id={cluster.uid}
                  className={style.commentsLink}
                  href="#"
                  title={`View ${formatAmounts(commentsAmount)} cluster ${pluralize(commentsAmount, 'comment', 'comments')}`}
                  onClick={event => onCommentsClick?.(event, { clusterId: cluster.uid })}
                >
                  <i className={classNames('icon-aytmfd-comments-v2', style.commentsIcon)} />
                </a>
              )}
            </li>
          )}
        </ul>
      </div>
    </div>
  )
}

ClusterPopup.displayName = 'ClusterPopup'

ClusterPopup.propTypes = {
  id: PropTypes.string.isRequired,
  cluster: PropTypes.shape({
    uid: PropTypes.string,
    title: PropTypes.string,
    color: PropTypes.string,
    border: PropTypes.array,
    editor: PropTypes.bool,
    points: PropTypes.array,
    stat: PropTypes.shape({
      respondents: PropTypes.number,
      points: PropTypes.number,
      comments: PropTypes.number
    })
  }),
  clusterEl: PropTypes.object,
  image: PropTypes.object,
  scale: PropTypes.number,
  config: PropTypes.object,
  batches: PropTypes.array,
  answer: answerPropType,
  userRoles: PropTypes.array,
  onEditCluster: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onCommentsClick: PropTypes.func
}

export default ClusterPopup
